import { useMemo, useState } from "react";
import { Redirect, useGlobalSearchParams } from "expo-router";
import { Spinner, View, YStack } from "tamagui";

import type { RouterInputs } from "~/utils/api";
import { api } from "~/utils/api";
import { Funnel } from "~/component/feature";
import { GlobalErrorScreen } from "~/component/global-error-screen";
import { SurveyHeader } from "~/component/layout";
import { useTrackOnce } from "~/hooks/use-track";

type QuestionnaireType = RouterInputs["survey"]["taskLookup"]["type"];

const Survey = () => {
  const type = useGlobalSearchParams().type as QuestionnaireType;
  useTrackOnce("Render Survey Page", { type });
  const [isFinal, setIsFinal] = useState(false);
  const {
    data,
    isFetching: isTaskLookupLoading,
    error: taskLookupError,
  } = api.survey.taskLookup.useQuery(
    {
      type,
    },
    {
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      enabled: !isFinal,
    },
  );

  const utils = api.useUtils();

  const {
    mutate,
    isLoading: isMutationLoading,
    isError: isMutationError,
    error: mutationError,
  } = api.survey.taskComplete.useMutation({
    onSuccess: (data) => {
      console.log(data?.scoresValues);
      if (data?.nextTaskId) {
        void utils.survey.taskLookup.refetch();
      } else {
        setIsFinal(true);
        void utils.home.tasksStatus.invalidate();
      }
    },
  });

  const isLoading = (isTaskLookupLoading || isMutationLoading) ?? false;

  const formItems = useMemo(() => {
    console.log(data?.task?.questionnaire?.name);

    const itemsWithQuestionnaire =
      data?.items?.map(({ id, answerOptions, text }) => {
        return {
          id,
          options: answerOptions,
          question: text,
          questionnaireId: data?.task?.questionnaire?.id,
          questionnaireTitle: data?.task?.questionnaire?.title,
          questionnaireName: data?.task?.questionnaire?.name,
        };
      }) ?? [];

    return itemsWithQuestionnaire;
  }, [data]);

  if (
    isFinal &&
    [
      "bat-12",
      "cips",
      "pss-10",
      "rse",
      "posttraumatic-growth",
      "asrs",
      "dss-b",
    ].includes(type)
  ) {
    return <Redirect href={`/slices/${type}`} />;
  }

  if (isFinal && type === "diagnostic") {
    return <Redirect href="/results/hmt" />;
  }

  if (isFinal && type !== "diagnostic") {
    return <Redirect href="/survey-transition" />;
  }

  if (isMutationError) {
    return <GlobalErrorScreen withHeader error={mutationError.message} />;
  }

  if (taskLookupError?.message === "NOT_FOUND") {
    return <Redirect href="/" />;
  }

  if (taskLookupError) {
    return <GlobalErrorScreen withHeader error={taskLookupError.message} />;
  }

  if (isLoading) {
    return (
      <YStack
        zIndex={100}
        position={"absolute"}
        justifyContent={"center"}
        height={"100dvh"}
        width={"100vw"}
      >
        <Spinner color={"$green-500"} size={"large"} />
      </YStack>
    );
  }

  return (
    <View flex={1}>
      <Funnel.Provider
        items={formItems}
        onSubmit={(answers) => {
          mutate({
            id: data?.task.id ?? "",
            answers,
          });
        }}
      >
        <SurveyHeader
          isFinal={isFinal}
          questionnaireProgress={data?.task?.questionnaire?.progressBar}
          questionnaireType={type}
        />
        <Funnel.Container>
          <Funnel.Question />
          <Funnel.Options isLoading={isLoading} />
        </Funnel.Container>
      </Funnel.Provider>
    </View>
  );
};

export default Survey;
